// Early-click guards (iteration 14 for the dropdown, generalized in iteration 22): make every
// click-activated widget work from the very first click, before boot() has created it.
//
// The customer embeds one `<script src=".../widget.js" async>`. boot() waits for DOM readiness
// (./dom-ready.ts) and only then creates the widgets, which is what binds their click-to-open. A
// click/tap on the shop's search box, search icon or "ask the assistant" link in between went to
// the shop's native search (or a plain link, or nowhere) and was lost. So boot() installs one
// guard per click widget FIRST (before the DOM wait): it swallows those early activations
// exactly like the widget will, remembers them, and is released right before that widget's
// create(); the product code then open()s the widget for them, one animation frame later.
// Prior art: sdk-search's src/index.dropdown.loader.ts (per-element listeners on a loader script).
//
// THE CORE (armGuard): delegated `document` CAPTURE listeners — boot() may run while the parser
// is still in <head>, before the element exists. Document-capture also runs before every
// target- and bubble-phase handler of the host (on the element or any ancestor below document),
// so handlers the host bound there — however early — never see a swallowed event. NOT
// guaranteed first overall: window-capture listeners, and document-capture listeners the host
// registered before widget.js ran, still run before ours (they see the event; our kill then stops
// everything after us). Installed all-or-nothing, removed idempotently, never throwing into
// boot(). Each registration has its OWN listeners, timers, release() and rearm(), so the widgets
// are released independently (iteration 23 creates them independently). Two safety caps, so a
// slow / failed boot can never leave the shop's element dead: CLICK_GUARD_LOADING_TIMEOUT_MS from
// install and — once boot() calls rearm() at DOM readiness — CLICK_GUARD_TIMEOUT_MS from then. A
// self-release forgets everything it recorded.
//
// THE REGISTRATIONS — each mirrors its widget's OWN binding (event set, what kills, what opens,
// how an element is matched), so the guard behaves exactly like the widget will; keep in sync
// with the SDKs:
//   - search DROPDOWN (installDropdownGuard; sdk-search 5.8 widgets/dropdown/widget.ts
//     `initializeInElement`): on an external <input> only `click` is killed (and opens) — the
//     mousedown still focuses it, so typing works; on anything else mousedown / mouseup / click /
//     touchstart / touchend are killed and mousedown / touchstart open. (The loader has the two
//     sets swapped.) pointerdown / pointerup are NOT killed, exactly like the widget. Matching:
//     each selector resolves to `document.querySelector()` (the FIRST match — the only element
//     the widget binds) and the event counts when its target is inside that element — not
//     `target.closest(selector)`: a shop with a second copy of the search box (mobile + desktop
//     header) would then have the copy the widget never binds swallowed. Focus is tracked, never
//     killed (dropdown only): a `focusin` on a guarded element is recorded only when it is
//     USER-initiated — within GESTURE_FOCUS_WINDOW_MS of a trusted keydown (Tab) or pointer /
//     mouse / touch down the guard saw since install. A focus that existed before install,
//     `autofocus`, or a script's .focus() never counts (a phantom open: onOpen + the Open
//     analytics event in the dropdown's experiment arm only). At release such a focus counts only while it still
//     holds (activeElement inside the element).
//   - search MODAL (installSearchModalGuard; sdk-search 5.8 widgets/modal/widget.ts
//     `initializeInElement`): the dropdown's non-input set on ANY element (mousedown / touchstart
//     kill + open; mouseup / click / touchend kill), first-match `querySelector` matching, no
//     focus tracking. Only the LATEST activation is replayed (each selector is its own
//     full-screen modal — two early taps on different triggers must not open two).
//   - ASSISTANT MODAL (installAssistantGuard; sdk-assistant 1.4.2 widgets/modal/widget.ts
//     `bindTriggers` + triggers.ts `findTrigger`): delegated document-capture listeners there too.
//     A trigger is matched by DELEGATION: the innermost element of the event's composed path that
//     `matches()` any trigger selector (each selector on its own, trimmed). What opens:
//       · a text-entry trigger (<textarea>, or <input> of type text / search / email / tel / url
//         / number / password, also inside a matched wrapper): a plain primary `mousedown`
//         (button 0, no modifier) — killed, and the rest of that gesture (its `mouseup` /
//         `click` within 1 s, and multi-click follow-ups / `dblclick`) killed too; a touch TAP
//         (`touchstart` observed passively, `touchend` ≤ 700 ms / ≤ 10 px, cancelable → killed);
//         `Enter` keydown (not composing) — killed;
//       · any other trigger: `click` with button 0 — killed — except a modified click on a link
//         (new tab / window keep their native meaning). Keyboard activation (Enter / Space on a
//         focused button / link) arrives as such a click (`detail === 0`) and counts as a
//         keyboard open, exactly like the widget.
//     Nothing else is touched. Launchers are inserted by the widget itself, so only `triggers`
//     are guarded. Not installed (by boot) when `config.chatbot.enabled` isn't true — the widget
//     then binds nothing and a trigger link must keep working as a link — nor when a trigger
//     selector is invalid (the widget would be inert); never on an arm without the assistant.
//     REPLAY / PREFLIGHT: the first activation is replayed as `widget.open({ opener, keyboard })`
//     — the opener / keyboard the widget itself would have used — one animation frame after
//     create(), at most once (products/assistant.ts). open() before the widget's preflight has
//     answered is SAFE and is exactly what a click right after create() does: open() builds the
//     dialog and requests the preflight (idempotent, cached per chatbot key); the dialog shows
//     its thinking state while it is pending, then the conversation — or, when preflight
//     denies / fails, the neutral unavailable state (+ `onUnavailable`, and `hide` / `invisible`
//     triggers hidden), as for any open before the answer. open() is a no-op on an inert widget
//     (`chatbot.enabled` false, a failed setup), so a swallowed early click is then lost — the
//     failure case. No deferral is needed and no sdk-assistant change.
//
// Deliberately import-free and pure DOM (like ./dom-ready.ts / ./light-styles.ts): it is meant
// to be lifted later into its own tiny guard entry loaded ahead of widget.js (PLAN.md iteration
// 14 follow-up), which would also cover the widget.js download window that these in-bundle
// guards cannot. It also unit-tests under plain Node with a stub document
// (packages/sdk/test/dropdown-guard.test.ts, click-guard.test.ts).

// Self-release deadline after DOM readiness (rearm()). Generous on purpose: it only bounds the
// failure case (boot never reaching the widget); a healthy boot releases the guard right after
// DOM readiness.
export const CLICK_GUARD_TIMEOUT_MS = 10_000;
// Self-release deadline from install, while the document may still be loading. Longer, so a
// slow DOMContentLoaded alone can't use up the window (activations are dead while armed, and
// would then be discarded); still bounded, should readiness never arrive at all.
export const CLICK_GUARD_LOADING_TIMEOUT_MS = 30_000;
// The iteration 14 names (the dropdown's guard uses the shared deadlines).
export const DROPDOWN_GUARD_TIMEOUT_MS = CLICK_GUARD_TIMEOUT_MS;
export const DROPDOWN_GUARD_LOADING_TIMEOUT_MS = CLICK_GUARD_LOADING_TIMEOUT_MS;
// How soon after a trusted gesture a focusin still counts as caused by it (dropdown). Tab /
// click focus happens in the same task as the keydown / mousedown; the slack covers a host
// handler that focuses the box in response to a tap on a search icon.
export const GESTURE_FOCUS_WINDOW_MS = 1_000;

export interface GuardTimeouts {
  // Deadline after rearm(); default CLICK_GUARD_TIMEOUT_MS.
  timeoutMs?: number;
  // Deadline from install until rearm(); default CLICK_GUARD_LOADING_TIMEOUT_MS.
  loadingTimeoutMs?: number;
  doc?: Document;
}

// ── The core ─────────────────────────────────────────────────────────────────────────────────

type Listener = [type: string, fn: (event: Event) => void, options: AddEventListenerOptions];

interface Armed {
  isReleased(): boolean;
  // Removes every listener and the timer (idempotent, never throws).
  unbind(): void;
  // Restarts the deadline as `timeoutMs` from now (no-op once released).
  rearm(): void;
}

// passive: false on the killing listeners — Chrome makes document-level touchstart / touchmove
// listeners passive by default, which would silently ignore preventDefault() (the tap would
// still reach the host / synthesize a click). Harmless on the mouse events. Watchers never
// preventDefault, so they stay passive.
const KILL: AddEventListenerOptions = { capture: true, passive: false };
const WATCH: AddEventListenerOptions = { capture: true, passive: true };

function armGuard(
  doc: Document,
  listeners: readonly Listener[],
  label: string,
  timeouts: GuardTimeouts,
  onTimeout: () => void,
): Armed {
  let released = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutMs = timeouts.timeoutMs ?? CLICK_GUARD_TIMEOUT_MS;

  const unbind = (): void => {
    if (released) return;
    released = true;
    clearTimeout(timer);
    // Guarded per listener so a release can never throw into boot().
    for (const [type, fn] of listeners) {
      try {
        doc.removeEventListener(type, fn, true);
      } catch {
        // Nothing sensible to do; keep removing the rest.
      }
    }
  };

  // All or nothing: if an add throws midway, the listeners already added are removed again
  // before rethrowing (boot() records the error) — a half-installed guard with no release
  // function and no timer would kill the element for good.
  try {
    for (const [type, fn, options] of listeners) doc.addEventListener(type, fn, options);
  } catch (error) {
    unbind();
    throw error;
  }

  const arm = (ms: number): void => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (released) return;
      unbind();
      onTimeout();
      console.warn(`[@raventic/sdk] ${label} not initialized within ${ms} ms — early-click guard released`);
    }, ms);
  };
  arm(timeouts.loadingTimeoutMs ?? CLICK_GUARD_LOADING_TIMEOUT_MS);

  return {
    isReleased: () => released,
    unbind,
    rearm() {
      if (!released) arm(timeoutMs);
    },
  };
}

const kill = (event: Event): void => {
  event.preventDefault();
  event.stopImmediatePropagation();
  event.stopPropagation();
};

// `target` may be anything an event can target: an element, a text node, the document,
// window… Only nodes can be inside an element (`contains()` throws on a non-Node).
function isNode(target: unknown): target is Node {
  return typeof target === "object" && target !== null && typeof (target as Node).nodeType === "number";
}

const toArray = (selectors: string | string[]): string[] => (Array.isArray(selectors) ? selectors : [selectors]);

// ── Search dropdown / search modal (first-match element, index results) ─────────────────────

// A guard whose widget's create() returns one Widget per selector (search dropdown / modal).
export interface IndexGuard {
  // Removes every listener and the timer, and returns the indices (into the selectors array, =
  // create()'s Widget[] order) of the widgets activated while armed: clicked / pressed, or (the
  // dropdown) focused by the user with that focus still holding now. Idempotent: later calls
  // return the same set and do nothing else. After a self-release on timeout the set is EMPTY —
  // the visitor has since had a working native element, so popping the widget open much later
  // would only surprise them.
  release(): ReadonlySet<number>;
  // The index of the LATEST click / press activation seen while armed (null: none, or a
  // self-release on timeout). The search modal replays only this one: each index is a separate
  // full-screen modal, and two early taps on different triggers must not stack two of them.
  lastActivation(): number | null;
  // Restarts the self-release deadline as `timeoutMs` from now. boot() calls it once the DOM is
  // ready. No-op once released.
  rearm(): void;
}
export type DropdownGuard = IndexGuard;
export type SearchModalGuard = IndexGuard;

export interface DropdownGuardOptions extends GuardTimeouts {
  // Whether an <input> target counts as the widget's "external input" (click-only event set).
  // The widget decides this as `!config.chatbot?.enabled && element instanceof
  // HTMLInputElement`; pass the chatbot half here. Default true.
  inputIsExternal?: boolean;
}

// event type → does it activate (open) the widget? Absent = not touched at all. Copied from the
// widgets' binding (see the header) — keep in sync with sdk-search.
const INPUT_EVENTS: Readonly<Record<string, boolean>> = { click: true };
const OTHER_EVENTS: Readonly<Record<string, boolean>> = {
  mousedown: true,
  mouseup: false,
  click: false,
  touchstart: true,
  touchend: false,
};
const KILL_TYPES = ["mousedown", "mouseup", "click", "touchstart", "touchend"];
// Observed only, never killed (dropdown): trusted gestures (timestamped) + focus.
const GESTURE_TYPES = ["keydown", "pointerdown", "mousedown", "touchstart"];

// The element the widget for `selector` will bind (null when absent, or for an invalid selector
// — the widget's own querySelector throws on that one, not us).
function boundElement(doc: Document, selector: string): Element | null {
  try {
    return doc.querySelector(selector);
  } catch {
    return null;
  }
}

const isExternalInput = (el: Element, inputIsExternal: boolean): boolean =>
  inputIsExternal && typeof HTMLInputElement !== "undefined" && el instanceof HTMLInputElement;

function installIndexGuard(
  selectors: string | string[],
  options: DropdownGuardOptions,
  label: string,
  trackFocus: boolean,
): IndexGuard {
  const list = toArray(selectors);
  const doc = options.doc ?? document;
  const inputIsExternal = trackFocus ? (options.inputIsExternal ?? true) : false;
  let clicked = new Set<number>();
  let focused = new Set<number>();
  let latest: number | null = null;
  // Date.now() of the last trusted gesture seen while armed; -Infinity = none yet.
  let lastGesture = -Infinity;
  let result: ReadonlySet<number> | null = null;

  // Indices whose bound element contains `node`.
  const matching = (node: unknown): number[] => {
    if (!isNode(node)) return [];
    const out: number[] = [];
    list.forEach((selector, index) => {
      const el = boundElement(doc, selector);
      if (el && el.contains(node)) out.push(index);
    });
    return out;
  };

  const onKillable = (event: Event): void => {
    const target = event.target;
    if (!isNode(target)) return;
    let killed = false;
    // Every selector is checked (no early return): two widgets bound to the same element both
    // open on the widget's own binding, so both are recorded here.
    list.forEach((selector, index) => {
      const el = boundElement(doc, selector);
      if (!el || !el.contains(target)) return;
      const events = isExternalInput(el, inputIsExternal) ? INPUT_EVENTS : OTHER_EVENTS;
      if (!(event.type in events)) return;
      killed = true;
      if (events[event.type]) {
        clicked.add(index);
        latest = index;
      }
    });
    if (killed) kill(event);
  };

  // Non-killing (dropdown): gesture timestamps + user-initiated focus. Registered BEFORE the
  // killing listeners, so a killed mousedown / touchstart is still seen as a gesture.
  const onWatch = (event: Event): void => {
    if (event.type !== "focusin") {
      if (event.isTrusted) lastGesture = Date.now();
      return;
    }
    if (Date.now() - lastGesture > GESTURE_FOCUS_WINDOW_MS) return;
    for (const index of matching(event.target)) focused.add(index);
  };

  const listeners: Listener[] = [
    ...(trackFocus ? [...GESTURE_TYPES, "focusin"].map((type): Listener => [type, onWatch, WATCH]) : []),
    ...KILL_TYPES.map((type): Listener => [type, onKillable, KILL]),
  ];
  const armed = armGuard(doc, listeners, label, options, () => {
    clicked = new Set();
    focused = new Set();
    latest = null;
  });

  return {
    release() {
      if (result) return result;
      armed.unbind();
      // A user-initiated focus counts only while it still holds.
      const holding = new Set(trackFocus ? matching(doc.activeElement) : []);
      const activated = new Set(clicked);
      for (const index of focused) if (holding.has(index)) activated.add(index);
      result = activated;
      return result;
    },
    lastActivation: () => latest,
    rearm: armed.rearm,
  };
}

// The search dropdown's guard (iteration 14 behaviour, unchanged): see the header.
export function installDropdownGuard(selectors: string | string[], options: DropdownGuardOptions = {}): DropdownGuard {
  return installIndexGuard(selectors, options, "dropdown", true);
}

// The search modal's guard: the dropdown's non-input event set on any element, no focus
// tracking (see the header).
export function installSearchModalGuard(selectors: string | string[], options: GuardTimeouts = {}): SearchModalGuard {
  return installIndexGuard(selectors, options, "search modal", false);
}

// ── Assistant modal (delegated matching, one activation) ────────────────────────────────────

// How the widget would have opened for the first early activation (its open() options).
export interface AssistantActivation {
  // Where focus returns on close: the innermost keyboard-focusable element between the target
  // and the trigger (else the trigger itself); null for a pointer / tap open of a text-entry
  // trigger (the widget never returns focus there — it would raise the on-screen keyboard).
  opener: HTMLElement | null;
  // Opened from the keyboard (Enter on a text field, a `detail === 0` click).
  keyboard: boolean;
}

export interface AssistantGuard {
  // Removes every listener and the timer; returns the FIRST activation seen while armed, or null
  // (none, or a self-release on timeout). Idempotent.
  release(): AssistantActivation | null;
  rearm(): void;
}

// The widget's constants (sdk-assistant widgets/modal/widget.ts + triggers.ts).
const GESTURE_WINDOW_MS = 1_000;
const TAP_MAX_MS = 700;
const TAP_MAX_MOVE_PX = 10;
const TEXT_ENTRY_TYPES = ["text", "search", "email", "tel", "url", "number", "password"];
const FOCUS_CANDIDATES = "a[href], button, input, select, textarea, [tabindex]";

const hasGlobal = (name: string): boolean => typeof (globalThis as Record<string, unknown>)[name] === "function";
const isA = (node: unknown, ctor: string): boolean =>
  hasGlobal(ctor) && node instanceof ((globalThis as unknown as Record<string, new () => unknown>)[ctor]!);

// An Element (duck-typed: the composed path also holds the document, shadow roots, window).
const isElement = (node: unknown): node is Element =>
  typeof node === "object" &&
  node !== null &&
  (node as Node).nodeType === 1 &&
  typeof (node as Element).matches === "function";

const safeMatches = (el: Element, selector: string): boolean => {
  try {
    return el.matches(selector);
  } catch {
    return false;
  }
};

// `input.type` reflects a missing or unknown type attribute as "text".
const isTextEntry = (node: unknown): boolean =>
  isA(node, "HTMLTextAreaElement") ||
  (isA(node, "HTMLInputElement") && TEXT_ENTRY_TYPES.indexOf((node as HTMLInputElement).type) >= 0);

const isLink = (node: unknown): boolean => isA(node, "HTMLAnchorElement") && (node as Element).hasAttribute("href");

// sdk-assistant focus.ts isFocusCandidate (without the layout check).
const isFocusCandidate = (node: unknown): node is HTMLElement => {
  if (!isA(node, "HTMLElement")) return false;
  const el = node as HTMLElement;
  try {
    return (
      el.matches(FOCUS_CANDIDATES) &&
      el.getAttribute("tabindex") !== "-1" &&
      !el.matches(":disabled") &&
      !(isA(el, "HTMLInputElement") && (el as HTMLInputElement).type === "hidden") &&
      el.closest("[hidden], [inert]") === null
    );
  } catch {
    return false;
  }
};

interface TriggerMatch {
  opener: HTMLElement | null;
  textEntry: boolean;
  link: boolean;
}

// The event's composed path (the target's ancestor chain where composedPath is missing).
function eventPath(event: Event): unknown[] {
  if (typeof event.composedPath === "function") return event.composedPath();
  const path: unknown[] = [];
  for (let node = event.target as Node | null; node; node = node.parentNode) path.push(node);
  return path;
}

// sdk-assistant triggers.ts findTrigger(), without launchers (none exist before create()).
function findTrigger(event: Event, selectors: readonly string[]): TriggerMatch | null {
  const path = eventPath(event);
  for (let i = 0; i < path.length; i++) {
    const node = path[i];
    if (!isElement(node) || !selectors.some((selector) => safeMatches(node, selector))) continue;
    const between = path.slice(0, i + 1);
    const focusable = between.find(isFocusCandidate);
    return {
      opener: focusable ?? (isA(node, "HTMLElement") ? (node as HTMLElement) : null),
      textEntry: between.some(isTextEntry),
      link: between.some(isLink),
    };
  }
  return null;
}

// sdk-assistant triggers.ts normalizeTriggers(): the trimmed selectors, or null when the widget
// would reject them (wrong shape / an invalid selector — it then stays inert; nothing to guard).
function triggerSelectors(triggers: unknown, doc: Document): string[] | null {
  if (!Array.isArray(triggers) && typeof triggers !== "string") return null;
  const out: string[] = [];
  for (const entry of Array.isArray(triggers) ? triggers : [triggers]) {
    const selector =
      typeof entry === "string"
        ? entry
        : typeof entry === "object" && entry !== null && !Array.isArray(entry)
          ? (entry as { selector?: unknown }).selector
          : undefined;
    if (typeof selector !== "string") return null;
    if (selector.trim() !== "") out.push(selector.trim());
  }
  const probe = doc.documentElement as Element | null;
  if (probe && typeof probe.matches === "function") {
    try {
      for (const selector of out) probe.matches(selector);
    } catch {
      return null;
    }
  }
  return out;
}

const isPlainPrimary = (mouse: MouseEvent): boolean =>
  mouse.button === 0 && !(mouse.ctrlKey || mouse.metaKey || mouse.altKey || mouse.shiftKey);

interface TouchStartPoint {
  x: number;
  y: number;
  at: number;
}

const touchPoint = (event: Event, list: "touches" | "changedTouches"): { x: number; y: number } | null => {
  const touch = (event as TouchEvent)[list]?.[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : null;
};

// Where and when a touch started — a tap candidate; null for a multi-finger touch.
const tapStart = (event: Event): TouchStartPoint | null => {
  if (((event as TouchEvent).touches?.length ?? 1) > 1) return null;
  return { ...(touchPoint(event, "touches") ?? { x: 0, y: 0 }), at: Date.now() };
};

// Whether a touchend concludes a tap: short, still, and cancelable.
const isTap = (start: TouchStartPoint, event: Event): boolean => {
  if (!event.cancelable || Date.now() - start.at > TAP_MAX_MS) return false;
  const end = touchPoint(event, "changedTouches");
  return end === null || Math.hypot(end.x - start.x, end.y - start.y) <= TAP_MAX_MOVE_PX;
};

const within = (at: number | null, ms: number): boolean => at !== null && Date.now() - at <= ms;

// The assistant modal's guard (see the header). `triggers` = the activation's `triggers` (the
// widget's own shape). Returns null when there is nothing to guard (the widget would be inert).
export function installAssistantGuard(triggers: unknown, options: GuardTimeouts = {}): AssistantGuard | null {
  const doc = options.doc ?? document;
  const selectors = triggerSelectors(triggers, doc);
  if (selectors === null || selectors.length === 0) return null;

  let activation: AssistantActivation | null = null;
  let result: AssistantActivation | null | undefined;
  // The widget's gesture state (see sdk-assistant widget.ts): a pointer open's remaining events
  // are swallowed for GESTURE_WINDOW_MS; multi-click follow-ups too.
  let mouseGestureAt: number | null = null;
  let multiClickAt: number | null = null;
  let touchStart: TouchStartPoint | null = null;

  const activate = (a: AssistantActivation): void => {
    if (activation === null) activation = a;
  };
  const clearGestures = (): void => {
    mouseGestureAt = null;
    multiClickAt = null;
    touchStart = null;
  };
  const isFollowUp = (event: Event): boolean => (event as MouseEvent).detail > 1 && within(multiClickAt, GESTURE_WINDOW_MS);

  const onMouseDown = (event: Event): void => {
    const mouse = event as MouseEvent;
    if (isFollowUp(event)) return kill(event);
    mouseGestureAt = null;
    if (mouse.detail <= 1) multiClickAt = null;
    if (!isPlainPrimary(mouse)) return;
    const match = findTrigger(event, selectors);
    if (match === null || !match.textEntry) return;
    kill(event);
    mouseGestureAt = Date.now();
    multiClickAt = Date.now();
    activate({ opener: null, keyboard: false });
  };

  const onMouseUp = (event: Event): void => {
    if (isFollowUp(event) || within(mouseGestureAt, GESTURE_WINDOW_MS)) kill(event);
  };

  const onDoubleClick = (event: Event): void => {
    if (within(multiClickAt, GESTURE_WINDOW_MS)) kill(event);
  };

  const onClick = (event: Event): void => {
    const mouse = event as MouseEvent;
    if (isFollowUp(event)) return kill(event);
    // The pointer click concluding a text-entry gesture (a keyboard click, detail 0, never does).
    if (mouse.detail > 0 && within(mouseGestureAt, GESTURE_WINDOW_MS)) {
      mouseGestureAt = null;
      return kill(event);
    }
    const match = findTrigger(event, selectors);
    if (match === null || match.textEntry || mouse.button !== 0) return;
    if (match.link && (mouse.ctrlKey || mouse.metaKey || mouse.shiftKey || mouse.altKey)) return;
    kill(event);
    const keyboard = mouse.detail === 0;
    if (!keyboard) multiClickAt = Date.now();
    activate({ opener: match.opener, keyboard });
  };

  const onTouchStart = (event: Event): void => {
    touchStart = null;
    const start = tapStart(event);
    if (start === null) return;
    const match = findTrigger(event, selectors);
    if (match !== null && match.textEntry) touchStart = start;
  };

  const onTouchEnd = (event: Event): void => {
    const start = touchStart;
    touchStart = null;
    if (start === null || !isTap(start, event)) return;
    kill(event);
    activate({ opener: null, keyboard: false });
  };

  const onKeyDown = (event: Event): void => {
    const key = event as KeyboardEvent;
    clearGestures();
    if (key.key !== "Enter" || key.isComposing) return;
    const match = findTrigger(event, selectors);
    if (match === null || !match.textEntry) return;
    kill(event);
    activate({ opener: match.opener, keyboard: true });
  };

  // The widget's own listener set and passive-ness (touchstart passive, touchend cancelable).
  const listeners: Listener[] = [
    ["mousedown", onMouseDown, KILL],
    ["mouseup", onMouseUp, KILL],
    ["click", onClick, KILL],
    ["dblclick", onDoubleClick, KILL],
    ["touchstart", onTouchStart, WATCH],
    ["touchend", onTouchEnd, KILL],
    ["touchcancel", clearGestures, WATCH],
    ["pointercancel", clearGestures, WATCH],
    ["keydown", onKeyDown, KILL],
  ];
  const armed = armGuard(doc, listeners, "assistant modal", options, () => {
    activation = null;
  });

  return {
    release() {
      if (result !== undefined) return result;
      armed.unbind();
      result = activation;
      return result;
    },
    rearm: armed.rearm,
  };
}

// Replays early activations one animation frame later (setTimeout 0 where rAF is missing): at
// DOM readiness the host layout may still be settling, and a widget positions / focuses once,
// on open. Failures are console.error'd only (the caller doesn't wait for them).
export function replayNextFrame(label: string, run: () => void): void {
  const guarded = (): void => {
    try {
      run();
    } catch (error) {
      console.error(`[@raventic/sdk] ${label} early open failed`, error);
    }
  };
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(guarded);
  else setTimeout(guarded, 0);
}
